import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { authStateSelector } from 'src/app/auth/store/auth.selectors';
import {
    CITY_WEBSITE_MODULE_KEY,
    OTHER_MODULE_KEY,
    ReportModule,
    ReportReference,
    ReportReferenceKind,
    ReportSnapshot,
} from 'src/app/global/components/dialogs/report-problem-dialog/report-problem.model';
import { NavigationItem } from 'src/app/global/global.model';
import {
    languageSelector, mainNavigationItemsSelector, organisationSelector, secondaryNavigationItemsSelector,
} from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { environment } from 'src/environments/environment';
import { DiagnosticsService, toSafePath } from '../diagnostics/diagnostics.service';

const REFERENCE_PATTERNS: { kind: ReportReferenceKind; pattern: RegExp }[] = [
    { kind: 'request', pattern: /^\/requests-v2\/manage\/details\/([^/]+)/ },
    { kind: 'permit', pattern: /^\/permits\/manage\/details\/([^/]+)/ },
    { kind: 'citizen', pattern: /^\/user-update\/([^/]+)/ },
];

export function toModuleKey(path: string): string {
    return path.replace(/^\/+|\/+$/g, '').replace(/\//g, '-') || 'home';
}

/** Navigation item whose path is the longest prefix of the current path. */
export function detectNavigationItem(path: string, items: NavigationItem[]): NavigationItem | undefined {
    return items.filter((item) => item?.path && (item.path === '/' ? path === '/' : path === item.path || path.startsWith(`${item.path}/`))).sort((a, b) => b.path.length - a.path.length)[0];
}

/** Only ids: names and emails of citizens stay out of the report. */
export function extractReferences(path: string): ReportReference[] {
    return REFERENCE_PATTERNS.map(({ kind, pattern }) => ({ kind, match: path.match(pattern) }))
        .filter(({ match }) => !!match)
        .map(({ kind, match }) => ({ kind, id: decodeURIComponent(match[1]) }));
}

@Injectable({
    providedIn: 'root',
})
export class ReportContextService {
    constructor(
        private store: Store<State>,
        private router: Router,
        private translate: TranslateService,
        private diagnostics: DiagnosticsService,
    ) {}

    /** Captures the page the reporter is on, at the moment the dialog opens. */
    snapshot(): Observable<ReportSnapshot> {
        return combineLatest([
            this.store.select(profileSelector),
            this.store.select(authStateSelector),
            this.store.select(organisationSelector),
            this.store.select(languageSelector),
            this.store.select(mainNavigationItemsSelector),
            this.store.select(secondaryNavigationItemsSelector),
        ]).pipe(
            take(1),
            map(([profile, auth, organisation, language, primary, secondary]) => {
                const path = toSafePath(this.router.url);
                const navigationModules = [...(primary ?? []), ...(secondary ?? [])];
                const detected = detectNavigationItem(path, navigationModules);
                const otherModule = { key: OTHER_MODULE_KEY, label: this.translate.instant('components.report-problem-dialog.modules.other') };
                const modules: ReportModule[] = [
                    ...navigationModules.filter((item) => item?.path).map((item) => ({ key: toModuleKey(item.path), label: item.text })),
                    { key: CITY_WEBSITE_MODULE_KEY, label: this.translate.instant('components.report-problem-dialog.modules.city-website') },
                    otherModule,
                ].filter((module, index, all) => all.findIndex((other) => other.key === module.key) === index);
                const orgTag = organisation?.tag ?? auth?.org ?? '';

                return {
                    organisation: { tag: orgTag, name: organisation?.name ?? orgTag },
                    detectedModule: detected ? { key: toModuleKey(detected.path), label: detected.text } : otherModule,
                    modules,
                    context: {
                        page: { path, references: extractReferences(path) },
                        admin: {
                            id: profile?.id ?? '',
                            name: [profile?.firstName, profile?.lastName].filter(Boolean).join(' '),
                            email: profile?.email ?? '',
                            roles: auth?.roles ?? [],
                        },
                        env: environment.label,
                        lang: language ?? this.translate.currentLang ?? '',
                        browser: {
                            userAgent: navigator.userAgent,
                            viewport: `${window.innerWidth}x${window.innerHeight}`,
                            screen: `${window.screen.width}x${window.screen.height}`,
                            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
                        },
                        openedAt: new Date().toISOString(),
                        recentErrors: this.diagnostics.recent(),
                    },
                };
            }),
        );
    }
}
